'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, ControlledInput, Typography } from 'components';
import { handlerEnter } from 'handlers/handlerEnter';
import { toaster } from 'helpers/toaster';
import useAuth from 'hooks/useAuth';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import * as yup from 'yup';
import YupPassword from 'yup-password';

type FormData = {
  email: string;
  submitedEmail: string;
};

YupPassword(yup);

export default function ForgotPassword() {
  const auth = useAuth();
  const router = useRouter();
  const t = useTranslations();

  const [rememberMe, setRememberMe] = useState(true);

  const schema = yup.object().shape({
    email: yup
      .string()
      .nullable()
      .email(t('messages.fields.email_validation'))
      .required(t('messages.fields.email_required'))
  });
  const searchParams = useSearchParams();
  const submitedEmail = searchParams.get('email') ?? '';

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      submitedEmail
    }
  });

  const handleForgotPassword = () => {
    const { email } = getValues();
    auth
      .forgotPassword(email)
      .then(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('email', email);
        searchParams.set('signUp', 'false');
        return router.push('/auth/reset-password?' + searchParams.toString());
      })
      .catch((err) => {
        notifyException(err);
        toaster.error(err?.message);
      });
  };

  const onSubmit = handleSubmit(handleForgotPassword);

  const autoCompleteHandler = useCallback(
    (tag: string) => {
      return rememberMe ? tag : 'off';
    },
    [rememberMe]
  );

  useEffect(() => {
    setValue('email', submitedEmail);
  }, [setValue, submitedEmail]);

  return (
    <>
      <div className="flex flex-col">
        <Typography variant="medium7xl" className={'text-tertiary-100'}>
          {t('auth.forgot_password.title')}
        </Typography>
        <Typography variant="light4" className="pt-2 text-tertiary-200">
          {t('auth.forgot_password.place_email')}
        </Typography>
      </div>
      <div className="flex flex-col gap-4 px-1 pt-8">
        <ControlledInput
          control={control}
          error={errors.email}
          label="E-mail:"
          type="email"
          name="email"
          autoComplete={autoCompleteHandler('email')}
          defaultValue={submitedEmail}
          classNameParent="w-full"
        />
        <Checkbox
          checked={rememberMe}
          onChange={setRememberMe}
          name="rememberMe"
          label={t('auth.forgot_password.remember_me')}
          className="py-2"
        />
        <div className="flex w-full flex-col gap-4">
          <Button
            onClick={onSubmit}
            onKeyDown={handlerEnter(onSubmit)}
            label={t('auth.forgot_password.submit')}
            className="w-full"
          />
          <Link
            href={`/auth/sign-in?${searchParams.toString()}`}
            className="flex cursor-pointer items-center justify-center text-base leading-normal text-secondary-link"
          >
            <Typography variant="regular4">{t('auth.forgot_password.return_to_login')}</Typography>
          </Link>
        </div>
      </div>
    </>
  );
}
